/**
 * <s-product-gallery> — PDP featured media stage with thumbnails.
 *
 * One horizontal scroll-snap track holds every media item. Mobile shoppers swipe it;
 * thumbnails jump to a slide instantly. Whichever slide is in view drives the active
 * thumbnail, the mobile counter, and pauses any video that scrolls out of view.
 *
 * Required markup:
 *   [data-gallery-track]                 — horizontal scroll-snap container.
 *   [data-gallery-slide][data-index]     — one per media item, inside the track.
 *   [data-gallery-thumb][data-index]     — optional thumbnail buttons.
 *   [data-gallery-dot][data-index]       — optional dot buttons (mobile position indicator).
 *   [data-gallery-prev] / [data-gallery-next] — optional arrow buttons (wrap around).
 *   [data-gallery-current]               — optional element showing the 1-based position.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #track;
  #slides = [];
  #thumbs = [];
  #dots = [];
  #current = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;

    this.#slides = [...this.#track.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.#dots = [...this.querySelectorAll('[data-gallery-dot]')];
    if (this.#slides.length < 2) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    for (const thumb of this.#thumbs) {
      thumb.addEventListener('click', this.#handleThumbClick, opts);
    }
    for (const dot of this.#dots) {
      dot.addEventListener('click', this.#handleThumbClick, opts);
    }
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.#step(-1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.#step(1), opts);
    this.addEventListener('keydown', this.#handleKeydown, opts);

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.#track,
      threshold: 0.6,
    });
    for (const slide of this.#slides) {
      this.#observer.observe(slide);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleThumbClick = (event) => {
    const index = Number(event.currentTarget.dataset.index);
    if (Number.isNaN(index)) return;
    this.#goTo(index, false);
  };

  #handleKeydown = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-gallery-thumb]')) return;
    const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const back = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
    if (!forward && !back) return;

    event.preventDefault();
    const rtl = getComputedStyle(this).direction === 'rtl';
    const horizontal = event.key === 'ArrowRight' || event.key === 'ArrowLeft';
    const step = (forward ? 1 : -1) * (rtl && horizontal ? -1 : 1);
    const next = (this.#current + step + this.#slides.length) % this.#slides.length;
    this.#goTo(next, false);
    this.#thumbs[next]?.focus();
  };

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) {
        this.#pauseMedia(entry.target);
        continue;
      }
      const index = Number(entry.target.dataset.index);
      if (!Number.isNaN(index)) this.#setActive(index);
    }
  };

  #step(direction) {
    const count = this.#slides.length;
    this.#goTo((this.#current + direction + count) % count, true);
  }

  #goTo(index, smooth) {
    const slide = this.#slides[index];
    if (!slide) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({
      left: slide.offsetLeft - this.#track.offsetLeft,
      behavior: smooth && !reduceMotion ? 'smooth' : 'instant',
    });
    this.#setActive(index);
  }

  #setActive(index) {
    this.#current = index;

    this.#thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    this.#dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    const counter = this.querySelector('[data-gallery-current]');
    if (counter) counter.textContent = String(index + 1);
  }

  #pauseMedia(slide) {
    for (const video of slide.querySelectorAll('video')) {
      if (!video.paused) video.pause();
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
